import React from "react";
import { noop } from "lodash";

interface UseIntersectOptions {
  rootRef?: React.RefObject<Element | null>;
  rootMargin?: string;
  threshold?: number | number[];
  once?: boolean;
}

export default ({ rootRef, rootMargin, threshold = 0, once = false }: UseIntersectOptions = {}) => {
  const [entry, setEntry] = React.useState<IntersectionObserverEntry | null>(null);
  const [node, setNode] = React.useState<Element | null>();

  const observer = React.useRef<IntersectionObserver | null>(null);
  const entryRef = React.useRef<IntersectionObserverEntry | null>(entry);

  React.useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    if (once && entryRef.current?.isIntersecting) {
      return noop;
    }

    const rootElement = rootRef?.current;

    observer.current = new IntersectionObserver(
      ([e]) => {
        setEntry(e);

        if (once && e.isIntersecting) {
          observer.current?.disconnect();
        }
      },
      {
        root: rootElement,
        rootMargin,
        threshold
      }
    );

    if (node) {
      observer.current.observe(node);
    }

    return () => observer.current?.disconnect();
  }, [node, rootRef, rootMargin, threshold, entryRef, once]);

  return [setNode, entry] as const;
};
